import { SHAPE_GEOMETRY, SHAPE_CATEGORIES, getShapeLabel } from './shapes';

/**
 * Crop to Shape (Publisher Picture Tools ▸ Crop ▸ Crop to Shape): `properties.cropShape` holds a shape id from the
 * Shapes gallery; the picture frame is clipped to that outline. Only closed shapes qualify (no lines, connectors,
 * brackets or the text box entry) — the same 100×100 geometry the shapes are drawn with is reused for the clip.
 */
export const isCropShape = (id) => {
  if (!id || id === 'textbox' || id === 'freeform' || id === 'rectangle') return false;
  const g = SHAPE_GEOMETRY[id];
  return !!g && g.kind !== 'line' && !g.open;
};

/** Gallery categories reduced to the shapes a picture can be cropped to. */
export const CROP_SHAPE_CATEGORIES = SHAPE_CATEGORIES
  .map((c) => ({ ...c, shapes: c.shapes.filter((s) => isCropShape(s.id)) }))
  .filter((c) => c.shapes.length > 0);

/** Quick picks shown first in the gallery. */
export const CROP_SHAPE_QUICK = ['circle', 'rounded-rect', 'diamond', 'hexagon', 'octagon', 'star-5', 'heart', 'plaque', 'tear', 'arrow-right'];

export const cropShapeLabel = (id) => (id ? getShapeLabel(id) : 'Rectangle');

/** Path data (100×100 units) for the clip, or null when the shape needs no path (ellipse / rounded rectangle). */
export const cropShapePath = (id) => {
  const g = SHAPE_GEOMETRY[id];
  if (!g) return null;
  if (g.kind === 'polygon') return `M${g.points.trim().split(/\s+/).join(' L')} Z`;
  if (g.kind === 'path') return g.d;
  return null;
};

/**
 * CSS clip-path value for a picture frame of `w`×`h` screen pixels. Ellipses and rounded rectangles use CSS basic
 * shapes; everything else references an inline <clipPath> (see CropShapeDefs) so the geometry scales with the frame.
 */
export const cropShapeClip = (id, w, h, cornerRadius, clipId) => {
  const g = SHAPE_GEOMETRY[id];
  if (!g || !isCropShape(id)) return null;
  if (g.kind === 'ellipse') return 'ellipse(50% 50% at 50% 50%)';
  if (g.kind === 'rect') {
    const r = g.rounded ? Math.max(cornerRadius || 0, Math.min(w, h) * 0.15) : (cornerRadius || 0);
    return `inset(0 round ${Math.max(0, r)}px)`;
  }
  return `url(#${clipId})`;
};

export const cropShapeNeedsDefs = (id) => {
  const g = SHAPE_GEOMETRY[id];
  return !!g && isCropShape(id) && g.kind !== 'ellipse' && g.kind !== 'rect';
};

export const cropShapeEvenOdd = (id) => !!SHAPE_GEOMETRY[id]?.evenodd;
